/**
 * Header bar for the plugin builder editor page.
 * Includes publish dialog that transforms into a live progress tracker.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  Upload,
  Loader2,
  Coins,
  EllipsisVertical,
  Settings,
  Bug,
  LayoutDashboard,
  Sun,
  Moon,
  Check,
  X,
  ExternalLink,
  Star,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useFavoriteStatus } from '../../favorites/hooks/useFavoriteStatus';
import type { PluginBuilderSession, VirtualFileSystem } from '../types';
import type { PublishStatus } from '../hooks/usePublishStatus';

interface PluginBuilderHeaderProps {
  session: PluginBuilderSession | null;
  sessionId: string;
  files: VirtualFileSystem | null;
  onPublish: () => Promise<{ submissionId: string } | null>;
  onUnpublish?: () => Promise<void>;
  onConfigure?: () => void;
  onReportProblem?: () => void;
  onSaveNameDescription?: (
    name: string,
    description: string,
    expandedDescription: string,
  ) => Promise<void>;
  isPublishing: boolean;
  publishStatus: PublishStatus;
  hasUnpublishedChanges: boolean;
  className?: string;
}

type PublishStep = 'packaging' | 'submitting' | 'reviewing' | 'building' | 'done';

function getPublishSteps(
  isPublishing: boolean,
  publishStatus: PublishStatus,
): { step: PublishStep; label: string; state: 'pending' | 'active' | 'done' | 'error' }[] {
  const submissionStatus = publishStatus.status;
  const hasSubmission = !!publishStatus.submissionId;

  // If we haven't started yet, all pending
  if (!isPublishing && !hasSubmission) {
    return [
      { step: 'packaging', label: 'Packaging files', state: 'pending' },
      { step: 'submitting', label: 'Submitting for review', state: 'pending' },
      { step: 'reviewing', label: 'AI reviewing', state: 'pending' },
      { step: 'building', label: 'Building', state: 'pending' },
      { step: 'done', label: 'Published!', state: 'pending' },
    ];
  }

  // Currently submitting (isPublishing = true, no submission yet)
  if (isPublishing && !hasSubmission) {
    return [
      { step: 'packaging', label: 'Packaging files', state: 'active' },
      { step: 'submitting', label: 'Submitting for review', state: 'pending' },
      { step: 'reviewing', label: 'AI reviewing', state: 'pending' },
      { step: 'building', label: 'Building', state: 'pending' },
      { step: 'done', label: 'Published!', state: 'pending' },
    ];
  }

  // We have a submission — determine step states from submission status
  const isRejected = submissionStatus === 'rejected';
  const isFailed = submissionStatus === 'failed';

  return [
    { step: 'packaging', label: 'Packaging files', state: 'done' },
    { step: 'submitting', label: 'Submitting for review', state: 'done' },
    {
      step: 'reviewing',
      label: isRejected ? 'Review rejected' : 'AI reviewing',
      state: isRejected ? 'error' : submissionStatus === 'reviewing' ? 'active' : 'done',
    },
    {
      step: 'building',
      label: isFailed ? 'Build failed' : 'Building',
      state: isFailed
        ? 'error'
        : submissionStatus === 'building'
          ? 'active'
          : submissionStatus === 'published'
            ? 'done'
            : 'pending',
    },
    {
      step: 'done',
      label: 'Published!',
      state: submissionStatus === 'published' ? 'done' : 'pending',
    },
  ];
}

function StepIcon({ state }: { state: 'pending' | 'active' | 'done' | 'error' }) {
  if (state === 'active') return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  if (state === 'done') return <Check className="h-4 w-4 text-green-500" />;
  if (state === 'error') return <X className="h-4 w-4 text-destructive" />;
  return <div className="h-4 w-4 rounded-full border-2 border-muted" />;
}

function getStatusSubtitle(
  sessionStatus: string | undefined,
  publishStatus: PublishStatus,
): string {
  switch (publishStatus.status) {
    case 'reviewing':
      return 'Under review...';
    case 'building':
      return 'Building...';
    case 'published':
      return 'Published';
    case 'rejected':
      return 'Review rejected';
    case 'failed':
      return 'Build failed';
    default:
      return sessionStatus === 'active' ? 'Editing' : (sessionStatus ?? 'Editing');
  }
}

export function PluginBuilderHeader({
  session,
  sessionId,
  files,
  onPublish,
  onUnpublish,
  onConfigure,
  onReportProblem,
  onSaveNameDescription,
  isPublishing,
  publishStatus,
  hasUnpublishedChanges,
  className,
}: PluginBuilderHeaderProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  // Snapshot terminal publish status so checkmarks persist after the page resets publishSubmissionId
  const [terminalSnapshot, setTerminalSnapshot] = useState<PublishStatus | null>(null);
  // Track whether the arche has been published (session.isPublished may be stale on first publish)
  const [justPublished, setJustPublished] = useState(false);
  const isPublished = session?.isPublished || justPublished;
  const publishedArchId = isPublished ? session?.archId : undefined;
  // Prefer slug for user-friendly URLs; fall back to archId
  const publishedArcheUrl = isPublished ? `/a/${session?.archeSlug || session?.archId}` : undefined;
  const favorite = useFavoriteStatus(publishedArchId || 'none');
  const [publishCost, setPublishCost] = useState<{
    isFirstPublish: boolean;
    totalCents: number;
    reviewFeeCents: number;
    listingFeeCents: number;
  } | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  // Local state for name/description in the publish dialog
  const [dialogName, setDialogName] = useState('');
  const [dialogDescription, setDialogDescription] = useState('');
  const [dialogExpandedDescription, setDialogExpandedDescription] = useState('');
  // Unpublish confirmation dialog
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);

  // Initialize dialog fields and fetch cost when dialog opens
  useEffect(() => {
    if (publishDialogOpen && !hasSubmitted && sessionId) {
      const name = session?.currentName || '';
      setDialogName(name === 'Untitled Arche' ? '' : name);
      setDialogDescription(session?.currentDescription || '');
      setDialogExpandedDescription(session?.currentExpandedDescription || '');
      setCostLoading(true);
      api
        .getPluginPublishCost(sessionId)
        .then((cost) => setPublishCost(cost))
        .catch(() => setPublishCost(null))
        .finally(() => setCostLoading(false));
    }
  }, [
    publishDialogOpen,
    hasSubmitted,
    sessionId,
    session?.currentName,
    session?.currentDescription,
    session?.currentExpandedDescription,
  ]);

  // Snapshot publish status when it reaches a terminal state so the dialog
  // can keep showing completed steps even after the page resets the submission ID.
  useEffect(() => {
    if (
      publishStatus.status &&
      ['published', 'rejected', 'failed'].includes(publishStatus.status)
    ) {
      setTerminalSnapshot(publishStatus);
      if (publishStatus.status === 'published') {
        setJustPublished(true);
      }
    }
  }, [publishStatus]);

  const isNameValid = dialogName.trim() !== '' && dialogName.trim() !== 'Untitled Arche';
  const isDescriptionValid = dialogDescription.trim() !== '';
  const isReadyToPublish = isNameValid && isDescriptionValid;

  const handlePublish = async () => {
    if (!isReadyToPublish) return;

    try {
      // Persist name/description before publishing — must complete before publish
      // reads session.current_description from the database
      await onSaveNameDescription?.(
        dialogName.trim(),
        dialogDescription.trim(),
        dialogExpandedDescription.trim(),
      );

      const result = await onPublish();
      if (result) {
        setHasSubmitted(true);
        setTerminalSnapshot(null);
      }
    } catch {
      // onPublish handles its own error toasts; this catch prevents
      // silent failures from onSaveNameDescription or unexpected errors.
    }
  };

  const handleUnpublish = async () => {
    if (!onUnpublish) return;
    setIsUnpublishing(true);
    try {
      await onUnpublish();
      setUnpublishDialogOpen(false);
      setJustPublished(false);
    } finally {
      setIsUnpublishing(false);
    }
  };

  const formatCost = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const hasFiles = files && Object.keys(files).length > 0;
  const hasManifest = files && files['fias-plugin.json'];
  const fileCount = files ? Object.keys(files).length : 0;

  // Determine if publish button should be disabled
  const isPublishDisabled =
    !hasFiles || !hasManifest || isPublishing || publishStatus.isPolling || !hasUnpublishedChanges;

  // Use the terminal snapshot when the live status has been reset but dialog is still open
  const effectiveStatus =
    terminalSnapshot && !publishStatus.submissionId ? terminalSnapshot : publishStatus;

  // Progress tracker state
  const showProgressTracker = hasSubmitted || effectiveStatus.isPolling || isPublishing;
  const steps = getPublishSteps(isPublishing, effectiveStatus);
  const isTerminal =
    effectiveStatus.status && ['published', 'rejected', 'failed'].includes(effectiveStatus.status);

  // Reset submission state when dialog closes after terminal state
  const handleDialogOpenChange = (open: boolean) => {
    setPublishDialogOpen(open);
    if (!open) {
      if (isTerminal) {
        setHasSubmitted(false);
      }
      setTerminalSnapshot(null);
    }
  };

  const statusSubtitle = getStatusSubtitle(session?.status, publishStatus);

  return (
    <div className={cn('flex items-center justify-between border-b px-4 py-2', className)}>
      {/* Left: Back and title */}
      <div className="flex items-center gap-3 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={() => navigate('/a/arche_plugin_builder')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold truncate max-w-[150px] sm:max-w-none">
            {session?.currentName || 'Untitled Arche'}
          </h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{statusSubtitle}</span>
          </div>
        </div>
      </div>

      {/* Center: Cost indicator — desktop */}
      <div className="hidden xl:flex items-center gap-2 text-sm text-muted-foreground">
        <Coins className="h-4 w-4" />
        <span>
          {formatCost(session?.totalCostCents || 0)} credits used
          {(() => {
            const aiCost = (session?.currentSettings?.totalAiInvokeCostCents as number) ?? 0;
            return aiCost > 0 ? ` (incl. ${formatCost(aiCost)} arche AI)` : '';
          })()}
        </span>
      </div>

      {/* Mobile: Compact cost badge */}
      <div className="flex xl:hidden items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
        <Coins className="h-3.5 w-3.5" />
        <span>{formatCost(session?.totalCostCents || 0)}</span>
      </div>

      {/* Right: Actions — desktop */}
      <div className="hidden xl:flex items-center gap-2">
        {onConfigure && (
          <Button variant="ghost" size="sm" onClick={onConfigure}>
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
        )}

        {onReportProblem && (
          <Button variant="ghost" size="sm" onClick={onReportProblem}>
            <Bug className="h-4 w-4 mr-2" />
            Report
          </Button>
        )}

        {publishedArcheUrl && (
          <Button variant="ghost" size="sm" onClick={() => navigate(publishedArcheUrl)}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View
          </Button>
        )}

        {publishedArchId && !favorite.isLoading && (
          <Button variant="ghost" size="sm" onClick={favorite.toggle}>
            <Star className={cn('h-4 w-4 mr-2', favorite.isFavorite && 'fill-current')} />
            {favorite.isFavorite ? 'On Worktop' : 'Add to Worktop'}
          </Button>
        )}

        {isPublished && onUnpublish && (
          <Button variant="ghost" size="sm" onClick={() => setUnpublishDialogOpen(true)}>
            Unpublish
          </Button>
        )}

        <Button variant="ghost" size="sm" onClick={() => navigate('/contributor/dashboard')}>
          <LayoutDashboard className="h-4 w-4 mr-2" />
          Dashboard
        </Button>

        <Button variant="ghost" size="icon" onClick={toggleTheme} title="Toggle theme">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <div className="h-5 w-px bg-border" />

        <Button
          size="sm"
          onClick={() => setPublishDialogOpen(true)}
          disabled={isPublishDisabled}
          title={!hasUnpublishedChanges ? 'No changes since last publish' : undefined}
        >
          {isPublishing || publishStatus.isPolling ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          {publishStatus.isPolling
            ? 'Publishing...'
            : !hasUnpublishedChanges
              ? 'Published'
              : 'Publish'}
        </Button>
      </div>

      {/* Right: Actions — mobile dropdown */}
      <div className="xl:hidden flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <EllipsisVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onConfigure && (
              <DropdownMenuItem onClick={onConfigure}>
                <Settings className="h-4 w-4 mr-2" />
                Configure
              </DropdownMenuItem>
            )}
            {onReportProblem && (
              <DropdownMenuItem onClick={onReportProblem}>
                <Bug className="h-4 w-4 mr-2" />
                Report Problem
              </DropdownMenuItem>
            )}
            {publishedArcheUrl && (
              <DropdownMenuItem onClick={() => navigate(publishedArcheUrl)}>
                <ExternalLink className="h-4 w-4 mr-2" />
                View Arche
              </DropdownMenuItem>
            )}
            {publishedArchId && !favorite.isLoading && (
              <DropdownMenuItem onClick={favorite.toggle}>
                <Star className={cn('h-4 w-4 mr-2', favorite.isFavorite && 'fill-current')} />
                {favorite.isFavorite ? 'On Worktop' : 'Add to Worktop'}
              </DropdownMenuItem>
            )}
            {isPublished && onUnpublish && (
              <DropdownMenuItem onClick={() => setUnpublishDialogOpen(true)}>
                Unpublish
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate('/contributor/dashboard')}>
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onClick={toggleTheme}>
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 mr-2" />
              ) : (
                <Moon className="h-4 w-4 mr-2" />
              )}
              Toggle Theme
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setPublishDialogOpen(true)}
              disabled={isPublishDisabled}
            >
              {isPublishing || publishStatus.isPolling ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {publishStatus.isPolling
                ? 'Publishing...'
                : !hasUnpublishedChanges
                  ? 'Published'
                  : 'Publish'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Unpublish Confirmation Dialog */}
      <Dialog open={unpublishDialogOpen} onOpenChange={setUnpublishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unpublish Arche</DialogTitle>
            <DialogDescription>
              This will remove your arche from the marketplace and revert it to draft status. You
              can re-publish it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnpublishDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleUnpublish} disabled={isUnpublishing}>
              {isUnpublishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Unpublish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog — doubles as progress tracker */}
      <Dialog open={publishDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showProgressTracker
                ? isTerminal
                  ? effectiveStatus.status === 'published'
                    ? 'Arche Published'
                    : 'Publishing Failed'
                  : 'Publishing Arche'
                : isPublished
                  ? 'Update Arche'
                  : 'Publish Arche'}
            </DialogTitle>
            <DialogDescription>
              {showProgressTracker
                ? isTerminal
                  ? effectiveStatus.status === 'published'
                    ? 'Your arche has been published successfully.'
                    : 'There was a problem publishing your arche.'
                  : 'Your arche is being processed. You can close this dialog — progress continues in the background.'
                : isPublished
                  ? 'Submit your changes for review. The updated version will replace the current one once approved.'
                  : 'Submit your arche for review. Once approved, it will be available on the marketplace.'}
            </DialogDescription>
          </DialogHeader>

          {showProgressTracker ? (
            /* Progress tracker view */
            <div className="py-4 space-y-3">
              {steps.map(({ step, label, state }) => (
                <div key={step} className="flex items-center gap-3">
                  <StepIcon state={state} />
                  <span
                    className={cn(
                      'text-sm',
                      state === 'active' && 'font-medium',
                      state === 'done' && 'text-muted-foreground',
                      state === 'error' && 'text-destructive font-medium',
                      state === 'pending' && 'text-muted-foreground/50',
                    )}
                  >
                    {label}
                  </span>
                </div>
              ))}

              {effectiveStatus.status === 'rejected' && effectiveStatus.errorMessage && (
                <div className="mt-3 p-3 rounded-md bg-destructive/10 text-sm text-destructive">
                  {effectiveStatus.errorMessage}
                </div>
              )}

              {effectiveStatus.status === 'published' && publishedArcheUrl && (
                <div className="mt-3 p-3 rounded-md bg-green-500/10 text-sm">
                  <a
                    href={publishedArcheUrl}
                    className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium hover:underline"
                  >
                    View your published arche
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          ) : (
            /* Pre-submit view */
            (() => {
              const isRepublish = publishCost ? !publishCost.isFirstPublish : false;
              return (
                <div className="py-4 space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="publish-name">Name</Label>
                      <Input
                        id="publish-name"
                        value={dialogName}
                        onChange={(e) => setDialogName(e.target.value)}
                        placeholder="Give your arche a name"
                      />
                      {!isNameValid && dialogName.trim() === 'Untitled Arche' && (
                        <p className="text-xs text-destructive">
                          Please choose a unique name for your arche.
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="publish-description">Short Description</Label>
                      <Textarea
                        id="publish-description"
                        value={dialogDescription}
                        onChange={(e) => setDialogDescription(e.target.value)}
                        placeholder="One-liner shown on marketplace cards"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="publish-expanded-description">About</Label>
                      <Textarea
                        id="publish-expanded-description"
                        value={dialogExpandedDescription}
                        onChange={(e) => setDialogExpandedDescription(e.target.value)}
                        placeholder="Full description for your arche's detail page (optional)"
                        rows={4}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Files:</span> {fileCount} file
                    {fileCount !== 1 ? 's' : ''}
                  </p>
                  {isRepublish ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Your arche will be repackaged and submitted for AI security review.
                      </p>
                      <p className="text-sm font-medium">
                        {costLoading
                          ? 'Loading...'
                          : `Review fee: $${formatCost(publishCost?.reviewFeeCents ?? 100)}`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Your arche will be packaged, submitted for AI security review, and listed on
                        the marketplace once approved.
                      </p>
                      <p className="text-sm font-medium">
                        {costLoading
                          ? 'Loading...'
                          : `Listing fee: $${formatCost(publishCost?.totalCents ?? 5000)} (includes review)`}
                      </p>
                    </>
                  )}
                </div>
              );
            })()
          )}

          <DialogFooter>
            {showProgressTracker ? (
              <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
                {isTerminal ? 'Close' : 'Close (continues in background)'}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handlePublish} disabled={isPublishing || !isReadyToPublish}>
                  {isPublishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Publish
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
